import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Skeleton } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { Card, CardActionArea } from "@mui/material";
import StoryCard from "../components/StoryCard";
import { fetchUserStories } from "../utils/api";
import { useTheme } from "@mui/material/styles";
import { Add as AddIcon } from "@mui/icons-material";
import { t } from "@lingui/macro";

const NewStoryCard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 140,
        height: 238,
        border: `2px dashed ${theme.palette.callToAction.main}`,
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "transparent",
        transition: "transform 0.1s, box-shadow 0.1s",
        "&:hover": { transform: "scale(1.05)", boxShadow: theme.shadows[8] },
        cursor: "pointer",
        margin: theme.spacing(0),
      }}
      onClick={() => navigate("/mystories/new")}
    >
      <CardActionArea
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: theme.palette.callToAction.main,
        }}
      >
        <AddIcon sx={{ fontSize: 40, color: theme.palette.callToAction.main, mb: 1 }} />
        <Typography variant="h6" align="center" sx={{ color: theme.palette.callToAction.main }} ml={1} mr={1}>
          {t`Write A New Story`}
        </Typography>
      </CardActionArea>
    </Card>
  );
};

const SkeletonStoryCard = () => {
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 140,
        height: 238,
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "rgba(0,0,0,0.1)",
      }}
    >
      <Skeleton variant="rectangular" width="100%" height="80%" />
      <Box sx={{ padding: 1 }}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
      </Box>
    </Card>
  );
};

const MyStoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    fetchUserStories()
      .then((data) => {
        const sortedStories = data.sort((a, b) => {
          // Extract the latest chapter update time for each story
          const latestChapterUpdateA = a.chapters
            ? Math.max(...Object.values(a.chapters).map(ch => ch.updatedAt?._seconds || 0), 0)
            : 0;
          const latestChapterUpdateB = b.chapters
            ? Math.max(...Object.values(b.chapters).map(ch => ch.updatedAt?._seconds || 0), 0)
            : 0;

          if (latestChapterUpdateA !== latestChapterUpdateB) {
            return latestChapterUpdateB - latestChapterUpdateA; // Sort by chapter updatedAt
          }

          // If no chapter updates, sort by story updatedAt
          const storyUpdateA = a.updatedAt?._seconds || 0;
          const storyUpdateB = b.updatedAt?._seconds || 0;

          if (storyUpdateA !== storyUpdateB) {
            return storyUpdateB - storyUpdateA;
          }

          // If no updatedAt, sort by createdAt
          const createdA = a.createdAt?._seconds || 0;
          const createdB = b.createdAt?._seconds || 0;

          if (createdA !== createdB) {
            return createdB - createdA;
          }

          // If no timestamps, sort alphabetically by title
          return a.title.localeCompare(b.title);
        });

        setStories(sortedStories);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, []);



  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "auto",
        padding: theme.spacing(2),
      }}
    >
      <Grid2
        container
        spacing={2}
        sx={{
          justifyContent: "flex-start",
          margin: "0 auto",
          width: "auto",
          maxWidth: "100%",
        }}
      >
        {/* New Story Card as the first grid item */}
        <Grid2
          item
          xs={12}
          sm={6}
          md={4}
          sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
        >
          <NewStoryCard />
        </Grid2>

        {loading
          ? // Render placeholder skeletons when loading
          Array.from(new Array(6)).map((_, index) => (
            <Grid2
              key={index}
              item
              xs={12}
              sm={6}
              md={4}
              sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
            >
              <SkeletonStoryCard />
            </Grid2>
          ))
          : stories.length === 0
            ? // Show text when no stories exist
            (
              <Grid2 item xs={12}>
                <Typography align="center" color="textSecondary">
                {t`No stories available. Start writing your first story!`}
                </Typography>
              </Grid2>
            )
            : // Render actual story cards
            stories.map((story) => (
              <Grid2
                key={story.id}
                item
                xs={12}
                sm={6}
                md={4}
                sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
              >
                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                  <StoryCard story={story} />
                </Box>
              </Grid2>
            ))}
      </Grid2>
    </Box>
  );
};

export default MyStoriesPage;
